import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'

import * as gql from './gql'
import type { TypeParams } from './types'

@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /* ---------------------------- Standard methods ---------------------------- */

  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }
      return gql.queries.actions.execute(context)
    },
  )

  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      // Query and variables are not very useful to present in most contexts
      // where attributes are used
      ec.models.Attribute.fromKey<TypeParams>('id', { name: 'ID' }),
      ec.models.Attribute.fromKey<TypeParams>('alias', { name: 'Alias' }),
      ec.models.Attribute.fromKey<TypeParams>('name', { name: 'Name' }),
      ec.models.Attribute.fromKey<TypeParams>('description', {
        name: 'Description',
      }),
    ])

  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.queries.action.executor({ ...context, actionIdentifier: { id } })
  }

  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.createAction.executor({
      ...context,
      input: gql.inputs.action.INPUT.getGqlFrom(input),
    })
  }

  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.updateAction.executor({
      ...context,
      actionIdentifier: { id },
      updates: gql.inputs.action.UPDATES.getGqlFrom(updates),
    })
  }

  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.deleteAction.executor({
      ...context,
      actionIdentifier: { id },
    })
  }

  /* ------------------------------ Extra methods ----------------------------- */

  /**
   * Renders an {@link ents.core.mosaic.Action.Query}
   *
   * If the query is correctly written, it should produce a
   * {@link ents.core.mosaic.Action.Rendering}. But this method does not check
   * if the query is correctly written, so any result is possible
   */
  public render(
    context: ents.core.admin.Context.ForZone,
    {
      query,
      variables,
    }: {
      readonly query: ents.core.mosaic.Action.Query
      readonly variables?: ents.core.dms.data.query.Variable.Lookup
    },
  ): rxjs.Observable<unknown> {
    return dms.common.gql.queries.data.execute({
      ...context,
      query: JSON.stringify({
        ...query,
        variables: {
          ...query.variables,
          ...variables,
        },
      }),
    })
  }

  /**
   * Executes an {@link ents.core.mosaic.Action} or
   * {@link ents.core.mosaic.Action.Rendering}
   *
   * To execute an {@link ents.core.mosaic.Action}, just pass the
   * {@link ents.core.mosaic.Action.Id} and the backend will find the stored
   * {@link ents.core.mosaic.Action} corresponding to that
   * {@link ents.core.mosaic.Action.Id} and render and execute that
   * {@link ents.core.mosaic.Action}
   */
  public test(
    context: ents.core.admin.Context.ForZone,
    {
      id,
      rendering,
    }: ExactlyOneOf<
      Readonly<{
        id: ents.core.mosaic.Action.Id
        rendering: ents.core.mosaic.Action.Rendering
      }>
    >,
  ): rxjs.Observable<
    readonly ents.core.mosaic.Action.Rendering.OfUiOperationsOnly[]
  > {
    // TODO: this causes some issue, because we moved some files, so we comment
    // it out, it is not used anyway.
    throw new Error('Not Implemented')
    // return rendering
    //   ? gql.mutations.testActionRendering.execute({
    //       ...context,
    //       actionRendering: rendering,
    //     })
    //   : gql.mutations.testAction.execute({
    //       ...context,
    //       actionId: id,
    //     })
  }
}
